import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {getPublicS3Url} from "../../../utils/s3PublicUrl"
import {
  followUpCategoryTabOptions,
  normalizeFollowUpCategory,
} from "./followUpCategories";

const statusColors = {
  // "Queries Received": { bgcolor: "#e0e7ff", color: "#3730a3" },
  Completed: { bgcolor: "#dbeafe", color: "#1d4ed8" },
  Resolved: { bgcolor: "#dcfce7", color: "#166534" },
  Pending: { bgcolor: "#fef3c7", color: "#92400e" },
  "On Hold": { bgcolor: "#ffedd5", color: "#9a3412" },
  NA: { bgcolor: "#e5e7eb", color: "#374151" },
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};
const creatorName = (record) => {
  const user = record?.doneBy || {};
  return [user.userFirstName, user.userLastName].filter(Boolean).join(" ") || "User";
};

const countByDirection = (records, direction) =>
  records.filter((record) => record.callDirection === direction).length;

const getTimeStamp = (value) => {
  if (!value) return 0;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortRecordsByCreatedAt = (records = []) =>
  [...records].sort((left, right) => {
    const leftTime = getTimeStamp(left?.createdAt);
    const rightTime = getTimeStamp(right?.createdAt);

    return rightTime - leftTime;
  });

const isGroupRecord = (record = {}) =>
  record?.callType === "GROUP" ||
  (record?.participants?.length || 0) > 1;

const getCallTypeLabel = (record = {}) => {
  if (isGroupRecord(record)) return "Group Call";
  if (record?.touchPointType === "IN_PERSON_MEET" || record?.callType === "IN_PERSON_MEET") return "In Person Meet";
  if (record?.touchPointType === "MESSAGE") return "Message";
  return "Call";
};

const getDirectionLabel = (value) => {
  if (value === "INCOMING") return "Incoming";
  return "Outgoing";
};

const getGroupParticipantCount = (member) => {
  const regionCount = Number(String(member?.region || "").match(/\d+/)?.[0]);

  return (
    member?.participantIds?.length ||
    member?.participants?.length ||
    (Number.isFinite(regionCount) ? regionCount : 0)
  );
};

const getMemberSectionLabel = (participant) =>
  participant?.committeeSection || participant?.committeeType || participant?.committeeName || "";

const sameMemberSet = (left = [], right = []) => {
  const leftSet = new Set(left.filter(Boolean));
  const rightSet = new Set(right.filter(Boolean));

  return (
    leftSet.size > 0 &&
    leftSet.size === rightSet.size &&
    [...leftSet].every((id) => rightSet.has(id))
  );
};

const getParticipantIds = (member) =>
  member?.participantIds?.length
    ? member.participantIds
    : (member?.participants || [])
        .map((participant) => participant?.memberId || participant?.userId || participant?._id || "")
        .filter(Boolean);

const getMemberDisplayName = (member, committeeGroups = []) => {
  const isGroupSummary =
    member?.isGroupCall || String(member?.memberId || "").startsWith("group:");
  const rawName = member?.groupLabel || member?.memberName || member?.name || "NEC Member";

  if (!isGroupSummary || /^All(\s|$)/i.test(rawName)) {
    return rawName;
  }

  const participantIds = getParticipantIds(member);
  const exactCommitteeGroup = committeeGroups.find((group) =>
    sameMemberSet(participantIds, group.memberIds)
  );

  if (exactCommitteeGroup) {
    return exactCommitteeGroup.title;
  }

  const allCommitteeMemberIds = committeeGroups.flatMap((group) => group.memberIds || []);
  if (sameMemberSet(participantIds, allCommitteeMemberIds)) {
    return "All Members";
  }

  const participantCount = getGroupParticipantCount(member);
  const listedNamesCount = rawName.split(",").filter((name) => name.trim()).length;
  const sectionLabels = Array.from(
    new Set((member?.participants || []).map(getMemberSectionLabel).filter(Boolean))
  );

  if (sectionLabels.length === 1 && participantCount >= 6) {
    return `All ${sectionLabels[0]}`;
  }

  if (participantCount >= 20 || listedNamesCount >= 20) {
    return "All Members";
  }

  return rawName;
};

const NOTE_PREVIEW_LENGTH = 90;

const NoteCell = ({ note }) => {
  const [expanded, setExpanded] = useState(false);
  const text = String(note || "-");
  const canToggle = text.length > NOTE_PREVIEW_LENGTH;
  const visibleText = expanded || !canToggle ? text : `${text.slice(0, NOTE_PREVIEW_LENGTH)}...`;

  return (
    <Box sx={{ maxWidth: 280, whiteSpace: "normal", wordBreak: "break-word" }}>
      <Typography variant="body2">{visibleText}</Typography>
      {canToggle && (
        <Button
          size="small"
          onClick={() => setExpanded((value) => !value)}
          sx={{
            mt: 0.3,
            p: 0,
            minWidth: 0,
            color: "#a44900",
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          {expanded ? "Less" : "More"}
        </Button>
      )}
    </Box>
  );
};

const NecMemberDetailsDrawer = ({ open, onClose, member, details, loading, month, year, committeeGroups = [] }) => {
  // const [tab, setTab] = useState("All");

  // const records = details?.records || [];
  // const selectedMember = details?.member || member || {};
  // const selectedMemberDisplayName = getMemberDisplayName(selectedMember, committeeGroups);
  // const timelineLabel = `${month || "All"}/${year}`;

  // const filteredRecords = useMemo(
  //   () =>
  //     tab === "All"
  //       ? records
  //       : records.filter((item) => normalizeFollowUpCategory(item.category) === tab),
  //   [records, tab]
  // );








  const [tab, setTab] = useState("All");

const records = details?.records || [];

const availableCategories = new Set(
  records
    .map((item) => normalizeFollowUpCategory(item.category))
    .filter(Boolean)
);

const filteredFollowUpCategoryTabOptions =
  followUpCategoryTabOptions.filter(
    (category) =>
      category === "All" || availableCategories.has(category)
  );

const selectedMember = details?.member || member || {};
const selectedMemberDisplayName = getMemberDisplayName(
  selectedMember,
  committeeGroups
);
const timelineLabel = `${month || "All"}/${year}`;

const sortedRecords = useMemo(() => sortRecordsByCreatedAt(records), [records]);

const filteredRecords = useMemo(
  () =>
    tab === "All"
      ? sortedRecords
      : sortedRecords.filter(
          (item) => normalizeFollowUpCategory(item.category) === tab
        ),
  [sortedRecords, tab]
);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 980 } } }}>
      <Box sx={{ p: 2.5, borderBottom: "1px solid #e2e8f0" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar src={selectedMember.image} sx={{ bgcolor: "#A92FDD" }}>
              {selectedMemberDisplayName?.[0] || "N"}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
             <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxHeight: 100,
                  overflowY: "auto",
                }}
              >
                {selectedMember?.participants?.map((participant) => (
                  <Chip
                    key={participant?.memberId}
                    size="small"
                    avatar={
                      <Avatar
                        src={getPublicS3Url(participant?.image || undefined)}
                        sx={{
                          width: 20,
                          height: 20,
                          fontSize: 10,
                        }}
                      >
                        {!participant.image &&
                          (participant.memberName || participant.name)
                            ?.charAt(0)
                            ?.toUpperCase()}
                      </Avatar>
                    }
                    label={participant.memberName || participant.name || "NEC Member"}
                    sx={{
                      fontWeight: 500,
                      height: 24,
                    }}
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {[selectedMember.designation, selectedMember.region].filter(Boolean).join(" | ")}
              </Typography>
              {/* <Typography variant="caption" color="text.secondary">
                {timelineLabel} | {countByDirection(records, "OUTGOING")} outgoing |{" "}
                {countByDirection(records, "INCOMING")} incoming | {countByDirection(records, "GROUP")} group | {records.length} touch-points
              </Typography> */}
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {/* <Button
              startIcon={<AddIcCallIcon />}
              variant="contained"
              onClick={() => setCallModalOpen(true)}
              sx={{ textTransform: "none", bgcolor: "#A92FDD" }}
            >
              Add Follow-Up
            </Button> */}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, pt: 2 }}>
      <Tabs
  value={tab}
  onChange={(_, value) => setTab(value)}
  variant="scrollable"
  scrollButtons="auto"
>
  {filteredFollowUpCategoryTabOptions.map((item) => (
    <Tab key={item} value={item} label={item} />
  ))}
</Tabs>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Typography fontWeight={900} sx={{ mb: 1 }}>
          Touch-point Records
        </Typography>
        <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                {[
                  "Date & Time",
                  "Call Type",
                  "Direction",
                  "Category",
                  "Conversation Notes",
                  "Query Status",
                  "Next Touch-point Date",
                  "Created By",
                  // "Action",
                ].map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 800 }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((record) => (
                <TableRow key={record._id}>
                  <TableCell>{formatDateTime(record.dateTime)}</TableCell>
                  <TableCell>{getCallTypeLabel(record)}</TableCell>
                  <TableCell>{getDirectionLabel(record.callDirection)}</TableCell>
                  <TableCell>{normalizeFollowUpCategory(record.category)}</TableCell>
                  <TableCell>
                    <NoteCell note={record.note} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={record.queryStatus || "NA"} sx={statusColors[record.queryStatus] || statusColors.NA} />
                  </TableCell>
                  <TableCell>{formatDateTime(record.nextFollowUpDate)}</TableCell>
                  <TableCell>{creatorName(record)}</TableCell>
                  {/* Action column hidden as requested. */}
                </TableRow>
              ))}
              {!loading && filteredRecords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>No follow-up records.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Drawer>
  );
};

export default NecMemberDetailsDrawer;
